"use client";
import {
  identityDetailsSchema,
  identityDetailsType,
  ParentProfileType,
} from "@/src/mocks";
import { reduceTypeToObj } from "@/src/utils/generic-utils";
import {
  Controller,
  FieldErrors,
  UseFormGetValues,
  UseFormRegister,
  UseFormTrigger,
} from "react-hook-form";
import FormContainer, { FormSubSectionTitle } from "./form-container";
import BaseButton from "@/src/components/landing-page/button/base-button";
import { BaseDropdown, FloatingLabelInput } from "@/src/components";
import FileUpload from "./fileUploadBtn";
import { handleParentFormChange } from "@/src/utils/storage";
import { useState } from "react";
import { toast } from "react-toastify";
import { uploadImage } from "@/src/services";
import UtilityUpload from "./UtilityUploadBtn";

export interface FormProps {
  nextStep: () => void;
  trigger: UseFormTrigger<ParentProfileType>;
  getValues: UseFormGetValues<ParentProfileType>;
  resetField: any;
  control: any;
  register: UseFormRegister<ParentProfileType>;
  isValid: boolean;
  errors: FieldErrors<ParentProfileType>;
  setValue?: any;
  handleChange?: () => void;
  currentGender?: any;
  currentIdentity?: any;
  currentMaritalStatus?: any;
  currentResident?: any;
  setCurrentGender?: (value: any) => void;
  setCurrentIdentity?: (value: any) => void;
  setCurrentMaritalStatus?: (value: any) => void;
  setCurrentResident?: (value: any) => void;
  imageUrl?: string;
  setImageUrl?: (value: string) => void;
  utilityBillUrl?: string;
  setUtilityBillUrl?: (value: string) => void;
  currentOccupation?: any;
  currentEmployment?: any;
  currentIncome?: any;
  currentPayment?: any;
  setCurrentOccupation?: (value: any) => void;
  setCurrentEmployment?: (value: any) => void;
  setCurrentIncome?: (value: any) => void;
  setCurrentPayment?: (value: any) => void;
  currentAccount?: any;
  currentBank?: any;
  setCurrentBank?: (value: any) => void;
  setCurrentAccount?: (value: any) => void;
}

export interface FormOneProp extends FormProps {
  gender: Record<string, string>;
  maritalStatus: Record<string, string>;
  residentialStatus: Record<string, string>;
  proofOfIdentity: Record<string, string>;
}

const identityDetailsLabel = [
  "Select gender",
  "Marital status",
  "Residential status",
  "Bank Verification Number (BVN)",
  "National Identity Number",
  "Select your proof of identity",
  "Upload of proof of identity selected",
  "Upload of utility bill",
];

const identityDetailsLabelMap = reduceTypeToObj<identityDetailsType>(
  identityDetailsLabel,
  identityDetailsSchema.describe().fields
);

const maritalStatusOptions = [
  { label: "Single", value: "single" },
  { label: "Married", value: "married" },
  // { label: "Divorced", value: "divorced" },
];

const genderOptions = [
  { label: "Male", value: "male" },
  { label: "Female", value: "female" },
];

const residentialStatusOptions = [
  { label: "Resident Individual", value: "resident_individual" },
  { label: "Non - Resident", value: "non_resident" },
  { label: "Foreign National ", value: "foreign_national " },
];

const proofOfIdentityOptions = [
  { label: "National ID", value: "national_id" },
  { label: "International Passport", value: "international_passport" },
  { label: "Voters Card", value: "voters_card" },
  { label: "Driving License", value: "driving_license" },
];

function FormOne({
  nextStep,
  trigger,
  getValues,
  control,
  register,
  errors,
  setValue,
  currentGender,
  currentIdentity,
  currentMaritalStatus,
  currentResident,
  setCurrentGender,
  setCurrentIdentity,
  setCurrentMaritalStatus,
  setCurrentResident,
  imageUrl,
  setImageUrl,
  utilityBillUrl,
  setUtilityBillUrl,
}: FormProps) {
  const values = getValues();
  const dotNotLabel = Object.keys(identityDetailsSchema.describe().fields).map(
    (el) => el
  );
  const [image, setImage] = useState<Blob[]>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [utilityBill, setUtilityBill] = useState<Blob[]>([]);
  const [isUtilityUploading, setIsUtilityUploading] = useState<boolean>(false);

  const onImageChange = async (file: Blob) => {
    if (file) {
      try {
        setIsUploading(true);
        const imgFormData = new FormData();
        imgFormData.append("file", file);
        setImage([file]);
        const response: any = await uploadImage(imgFormData);
        toast.success(response?.data?.message);
        setValue("proofOfIdentityDocumentURL", response.data.data.url);
        setImageUrl && setImageUrl(response.data.data.url);
        trigger("proofOfIdentityDocumentURL");
        setIsUploading(false);
      } catch (err: any) {
        setIsUploading(false);
        console.log(err);
      }
    }
  };

  const onUtilityBillChange = async (file: Blob) => {
    if (file) {
      try {
        setIsUtilityUploading(true);
        const utilityBillFormData = new FormData();
        utilityBillFormData.append("file", file);
        setUtilityBill([file]);
        const response: any = await uploadImage(utilityBillFormData);
        toast.success(response?.data?.message);
        setValue("utilityBillDocumentURL", response.data.data.url);
        setUtilityBillUrl && setUtilityBillUrl(response.data.data.url);
        trigger("utilityBillDocumentURL");
        setIsUtilityUploading(false);
      } catch (err: any) {
        setIsUtilityUploading(false);
        console.log(err);
      }
    }
  };

  async function onValidate(): Promise<void> {
    try {
      await identityDetailsSchema.validate(values, { abortEarly: false });

      nextStep();
    } catch (error: any) {
      // @ts-ignore
      trigger(dotNotLabel);
      console.error(error.message);
    }
  }

  return (
    <section>
      <FormContainer
        title="Tell us more about yourself"
        subTitle="This is an important part for us to know you as the policy holder."
      >
        <form>
          <div className="py-2">
            <FormSubSectionTitle title="Identity details" />
            <div className="my-6">
              <div className="mb-5">
                <Controller
                  control={control}
                  name={"gender"}
                  render={() => (
                    <BaseDropdown
                      w="100%"
                      placeholder={identityDetailsLabelMap.gender}
                      onChange={handleParentFormChange(
                        "gender",
                        getValues,
                        setValue,
                        trigger,
                        setCurrentGender
                      )}
                      error={errors?.gender?.message}
                      options={genderOptions}
                      defaultValue={genderOptions.find(
                        (opt) => opt.value === currentGender?.value
                      )}
                    />
                  )}
                />
              </div>
              <div className="mb-5">
                <Controller
                  control={control}
                  name={"maritalStatus"}
                  render={() => (
                    <BaseDropdown
                      w="100%"
                      placeholder={identityDetailsLabelMap.maritalStatus}
                      onChange={handleParentFormChange(
                        "maritalStatus",
                        getValues,
                        setValue,
                        trigger,
                        setCurrentMaritalStatus
                      )}
                      error={errors?.maritalStatus?.message}
                      options={maritalStatusOptions}
                      defaultValue={maritalStatusOptions.find(
                        (opt) => opt.value === currentMaritalStatus?.value
                      )}
                    />
                  )}
                />
              </div>
              <div className="mb-5">
                <Controller
                  control={control}
                  name={"residentialStatus"}
                  render={() => (
                    <BaseDropdown
                      w="100%"
                      placeholder={identityDetailsLabelMap.residentialStatus}
                      onChange={handleParentFormChange(
                        "residentialStatus",
                        getValues,
                        setValue,
                        trigger,
                        setCurrentResident
                      )}
                      defaultValue={residentialStatusOptions.find(
                        (opt) => opt.value === currentResident?.value
                      )}
                      error={errors?.residentialStatus?.message}
                      options={residentialStatusOptions}
                    />
                  )}
                />
              </div>
              <div className="mb-5">
                <FloatingLabelInput
                  label={identityDetailsLabelMap.bvn}
                  id="identityDetails.bvn"
                  type="number"
                  placeholder={identityDetailsLabelMap.bvn}
                  {...register("bvn")}
                  error={errors?.bvn?.message}
                />
              </div>

              <div className="mb-5">
                <Controller
                  control={control}
                  name={"proofOfIdentity"}
                  render={() => (
                    <BaseDropdown
                      w="100%"
                      placeholder={identityDetailsLabelMap.proofOfIdentity}
                      onChange={handleParentFormChange(
                        "proofOfIdentity",
                        getValues,
                        setValue,
                        trigger,
                        setCurrentIdentity
                      )}
                      error={errors?.proofOfIdentity?.message}
                      options={proofOfIdentityOptions}
                      defaultValue={proofOfIdentityOptions.find(
                        (opt) => opt.value === currentIdentity?.value
                      )}
                    />
                  )}
                />
              </div>
              <div className="mb-5">
                <Controller
                  control={control}
                  name={"proofOfIdentityDocumentURL"}
                  render={() => (
                    <FileUpload
                      file={image}
                      setFile={setImage}
                      onChange={onImageChange}
                      isLoading={isUploading}
                      imageUrl={imageUrl}
                      // error={errors?.proofOfIdentityDocumentURL?.message}
                    />
                  )}
                />
                {errors?.proofOfIdentityDocumentURL?.message && (
                  <p className="text-xs text-red-500">
                    {errors?.proofOfIdentityDocumentURL?.message}
                  </p>
                )}
              </div>
              {/* "Upload of proof of identity selected"  */}
              <div className="mb-5">
                <UtilityUpload
                  file={utilityBill}
                  setFile={setUtilityBill}
                  onChange={onUtilityBillChange}
                  isLoading={isUtilityUploading}
                  imageUrl={utilityBillUrl}
                  // error={errors?.utilityBillDocumentURL?.message}
                />
                {errors?.utilityBillDocumentURL?.message && (
                  <p className="text-xs text-red-500">
                    {errors?.utilityBillDocumentURL?.message}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="my-6">
            <BaseButton onClick={onValidate} fullWidth>
              Proceed
            </BaseButton>
          </div>
        </form>
      </FormContainer>
    </section>
  );
}

export default FormOne;
